import { isoDateInZone, type Task } from '@cleartask/shared';
import { formatGroupDate } from '../../lib/datetime.js';
import { useTimeZone } from '../../session.js';
import { TaskChip } from './TaskChip.js';

interface AgendaViewProps {
  days: number[];
  byDay: Map<string, Task[]>;
  now: number;
  onOpen: (task: Task) => void;
}

/**
 * Visao em lista (spec 9.4).
 *
 * Mostra apenas os dias que TEM tarefa. Uma agenda que lista 30 dias, a
 * maioria vazia, obriga a rolar por nada — a grade do mes ja existe para quem
 * quer enxergar o vazio.
 *
 * Nao ha arrastar aqui: sem eixo de tempo, nao existe alvo para onde soltar.
 * Reagendar nesta visao e pelo painel de detalhe.
 */
export function AgendaView({ days, byDay, now, onOpen }: AgendaViewProps) {
  const timeZone = useTimeZone();

  const comTarefas = days
    .map((day) => {
      const date = isoDateInZone(timeZone, day);
      return { day, tarefas: byDay.get(date) ?? [] };
    })
    .filter((entry) => entry.tarefas.length > 0);

  if (comTarefas.length === 0) {
    return (
      <div className="rounded bg-surface p-6 text-center shadow-card">
        <p className="font-medium">Nada agendado nos próximos 30 dias.</p>
        <p className="mt-1 text-sm text-text-mut">
          Dê um prazo a uma tarefa para ela aparecer aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      {comTarefas.map(({ day, tarefas }) => (
        <section key={day} className="overflow-hidden rounded bg-surface shadow-card">
          <h3 className="border-b border-border px-3 py-2 text-sm font-medium text-text-mut">
            {formatGroupDate(day, now, timeZone)}
          </h3>

          <div className="flex flex-col gap-1 p-2">
            {tarefas.map((task) => (
              <TaskChip key={task.id} task={task} now={now} onOpen={onOpen} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
